import { computed, extendObservable, IArrayWillSplice, intercept, IObservableArray, isArrayLike, observable } from 'mobx';

import { ClientLogger } from 'bernie-client';
import { getErrorString } from 'bernie-logger';
import { merge, SerializedData, Store } from 'bernie-core';

import { STORE_EVENTS } from 'stores/events/store-events';

import { SearchCriteriaUtils } from './search-criteria-utils';

import { RoomModel } from './room-model';

const { withoutKeys } = merge;

const logger = ClientLogger.getLoggerWithIdentifier('pwa.store.searchCriteriaModel');

export class SearchCriteriaModel extends Store {
  public static MIDNIGHT_BOOKING_OFFSET_IN_HOURS = 4;
  public static NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

  constructor(state: SerializedData = {}) {
    super({}, null);

    // handle updates like someModel.rooms = []
    intercept(this, 'rooms', change => {
      try {
        if (!change.newValue || !isArrayLike(change.newValue)) {
          change.newValue = [];
        }

        this.rooms.replace(change.newValue);
      } catch (error) {
        const changeMessage = `change.newValue="${typeof change.newValue}"`;
        const errorString = getErrorString(error);
        logger.logEvent(STORE_EVENTS.STORE_INTERCEPT_VALUE_ERROR, errorString, changeMessage);
      }

      return null;
    });

    // handle individual element updates from calls like replace() and push()
    intercept(this.rooms, (change: IArrayWillSplice<RoomModel | any>) => {
      try {
        if (change.added && isArrayLike(change.added)) {
          change.added = change.added.map(item => (item instanceof RoomModel === false ? new RoomModel(item) : item));
        }
      } catch (error) {
        const changeMessage = `change.added="${typeof change.added}"`;
        const errorString = getErrorString(error);
        logger.logEvent(STORE_EVENTS.STORE_INTERCEPT_VALUE_ERROR, errorString, changeMessage);
      }

      return change;
    });

    Object.assign(this, SearchCriteriaModel.acceptedKeys(state));
    this.setDefaultRooms(state);
  }

  public hydrate(data: SerializedData) {
    if (typeof data === 'object' && data !== null) {
      data = SearchCriteriaModel.acceptedKeys(data);

      extendObservable(this, data);
      this.setDefaultRooms(data);
    }
  }

  // Should not use `null` as a default; may deserialize as empty string.
  public static DEFAULTS: any = {
    accessibility: [],
    amenities: [],
    chain: '',
    deals: [],
    daysInFuture: '',
    destination: '',
    endDate: SearchCriteriaUtils.getTomorrow(),
    group: '',
    guestRating: '',
    hotelName: '',
    latLong: '',
    lodging: [],
    paymentType: [],
    poi: '',
    price: [],
    regionId: '',
    roomIndex: '',
    rooms: [],
    selected: '',
    sort: 'recommended',
    star: [],
    startDate: SearchCriteriaUtils.getToday(),
    stayLength: '',
    theme: '',
    travelerType: [],
    useRewards: '',
    userIntent: '',
    bedroomFilter: [],
    propertyStyle: [],
    misId: '',
    rewards: [],
    multiItemCarPickUpTime: '1030',
    multiItemCarDropOffTime: '1030',
    commissiontier: [],
    agencyBusinessModel: [],
    mealPlan: []
  };

  @observable
  public accessibility;
  @observable
  public amenities;
  @observable
  public chain?: string;
  @observable
  public daysInFuture?: string;
  @observable
  public destination: string;
  @observable
  public endDate: string;
  @observable
  public group: string;
  @observable
  public guestRating: string;
  @observable
  public hotelName: string;
  @observable
  public latLong?: string;
  @observable
  public localDateFormat: string;
  @observable
  public lodging;
  @observable
  public paymentType;
  @observable
  public poi: string;
  @observable
  public price: string[];
  @observable
  public regionId: string;
  @observable
  public roomIndex;
  @observable
  public rooms: IObservableArray<RoomModel>;
  @observable
  public selected;
  @observable
  public semdtl;
  @observable
  public sort;
  @observable
  public star: string[];
  @observable
  public startDate: string;
  @observable
  public stayLength?: string;
  @observable
  public swpToggleOn;
  @observable
  public theme: string;
  @observable
  public travelerType;
  @observable
  public useRewards: string;
  @observable
  public userIntent: string;
  @observable
  public bedroomFilter: string[];
  @observable
  public deals;
  @observable
  public propertyStyle;
  @observable
  public multiProp: boolean;
  @observable
  public misId: string;
  @observable
  public referrerUrl: string;
  @observable
  public rewards: string[];
  @observable
  public multiItemCarPickUpTime: string;
  @observable
  public multiItemCarDropOffTime: string;
  @observable
  public commissionTier;
  @observable
  public agencyBusinessModel;
  @observable
  public mealPlan;

  @computed
  public get adults(): number {
    if (this.rooms && this.rooms.length > 0) {
      return this.rooms.reduce((base, room) => base + (room.adults || 0), 0);
    }

    return 0;
  }

  @computed
  public get children(): number {
    if (this.rooms && this.rooms.length > 0) {
      return this.rooms.reduce((base, room) => base + ((room.children && room.children.length) || 0), 0);
    }

    return 0;
  }

  @computed
  get guestCount(): number {
    return this.adults + this.children;
  }

  @computed
  get isDateless(): boolean {
    return !this.startDate && !this.endDate;
  }

  public clone(): SearchCriteriaModel {
    return new SearchCriteriaModel(this.toJSON());
  }

  private setDefaultRooms(state: any = {}) {
    if (!state.rooms || (state.rooms.length === 0 && this.rooms.length === 0)) {
      this.rooms.push(new RoomModel({ children: [], adults: 2 }));
    }
  }

  private static acceptedKeys(data: any = {}): any {
    return withoutKeys(['adults', 'children']).merge({}, data);
  }

  public toJSON(ignoreAdditionalKeys?: string[] | string): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys && ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }
    const json = super.toJSON(ignoreAdditionalKeys.concat('rooms'));

    json.rooms = (this.rooms && this.rooms.map(room => room.toJSON())) || [];

    return json;
  }
}
